import { useEffect, useRef, useState } from 'react';
import { buildReportFileName, generateReportPDF } from '../../../services/clinicalReportService';
import { findProfessional } from './reportCompany';

/** Espera as imagens do template terminarem de carregar (ou falhar). */
function waitForImages(element) {
  const images = Array.from(element.querySelectorAll('img'));
  return Promise.all(images.map((image) => (
    image.complete
      ? Promise.resolve()
      : new Promise((resolve) => { image.onload = resolve; image.onerror = resolve; })
  )));
}

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

/**
 * Exportação em PDF de um módulo do atendimento. O template só é montado
 * (fora da tela) durante a geração: o card renderiza
 * `{job && <HiddenReport><Template ref={ref} {...paperProps} {...job} /></HiddenReport>}`
 * e chama `start()` no botão.
 *
 * `prepare`: função (pode ser assíncrona) que devolve os dados do template,
 * por exemplo depois de baixar as fotos; `null` cancela a exportação.
 * `reportType`: sufixo do nome do arquivo (`Nome_tipo_AAAA-MM-DD.pdf`).
 *
 * @returns {{ ref, job, exporting, start, paperProps }}
 */
export default function useReportExport({ patientName, professionalId, companyConfig, reportType, prepare }) {
  const ref = useRef(null);
  const [job, setJob] = useState(null);
  const [exporting, setExporting] = useState(false);

  async function start() {
    if (exporting) return;
    setExporting(true);
    try {
      const data = prepare ? await prepare() : {};
      if (!data) {
        setExporting(false);
        return;
      }
      setJob(data);
    } catch (error) {
      setExporting(false);
    }
  }

  useEffect(() => {
    if (!job || !ref.current) return undefined;
    let active = true;
    const element = ref.current;
    waitForImages(element)
      .then(nextFrame)
      .then(() => generateReportPDF(element, buildReportFileName(patientName, reportType)))
      .catch(() => {})
      .finally(() => {
        if (!active) return;
        setJob(null);
        setExporting(false);
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job]);

  const paperProps = {
    companyData: companyConfig,
    professional: findProfessional(companyConfig, professionalId),
    patientName,
  };

  return { ref, job, exporting, start, paperProps };
}
